import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, Chip, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, FormControlLabel, Switch, Snackbar, Alert,
  CircularProgress, Collapse,
} from '@mui/material';
import {
  AccountTree as AccountTreeIcon,
  Add as AddIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  Lock as LockIcon,
  AutoFixHigh as AutoIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapsedIcon,
  Close as CloseIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  dreService, ChartOfAccount, DreGroup, DreNature,
  DRE_GROUP_COLORS, DRE_GROUP_LABELS,
} from '../../services/dreService';

// =============================================================================
// Tipos do form
// =============================================================================

const EMPTY_FORM: Partial<ChartOfAccount> = {
  unit_id: null,
  code: '',
  name: '',
  description: '',
  parent_id: null,
  level: 1,
  is_synthetic: false,
  dre_group: 'REVENUE',
  nature: 'OPERATIONAL',
  sign: 1,
  sort_order: 0,
  is_active: true,
};

// =============================================================================
// Página principal
// =============================================================================

const ChartOfAccounts: React.FC = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<ChartOfAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<ChartOfAccount | null>(null);
  const [creating, setCreating] = useState<{ parentId: string | null } | null>(null);
  const [form, setForm] = useState<Partial<ChartOfAccount>>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false, message: '', severity: 'info',
  });

  const fetchAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dreService.listChartOfAccounts();
      setAccounts(data);
      // Expande automaticamente as raízes
      setExpanded(new Set(data.filter((a) => a.level === 1).map((a) => a.id)));
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar plano de contas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAccounts(); }, []);

  // Árvore por parent_id
  const tree = useMemo(() => {
    const byParent = new Map<string | null, ChartOfAccount[]>();
    for (const a of accounts) {
      const key = a.parent_id;
      if (!byParent.has(key)) byParent.set(key, []);
      byParent.get(key)!.push(a);
    }
    byParent.forEach((arr) => arr.sort((a: ChartOfAccount, b: ChartOfAccount) => a.sort_order - b.sort_order || a.code.localeCompare(b.code)));
    return byParent;
  }, [accounts]);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleEdit = (a: ChartOfAccount) => {
    setEditing(a);
    setForm({ ...a });
  };

  const handleCreate = (parentId: string | null) => {
    const parent = parentId ? accounts.find((a) => a.id === parentId) : null;
    setCreating({ parentId });
    setForm({
      ...EMPTY_FORM,
      parent_id: parentId,
      level: parent ? parent.level + 1 : 1,
      dre_group: parent ? parent.dre_group : 'REVENUE',
      nature: parent ? parent.nature : 'OPERATIONAL',
      sign: parent ? parent.sign : 1,
      sort_order: parent ? (tree.get(parentId)?.length || 0) * 10 + 10 : 100,
    });
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await dreService.updateChartOfAccount(editing.id, form);
        setSnackbar({ open: true, message: 'Conta atualizada', severity: 'success' });
      } else {
        await dreService.createChartOfAccount(form as ChartOfAccount);
        setSnackbar({ open: true, message: 'Conta criada', severity: 'success' });
      }
      setEditing(null);
      setCreating(null);
      setForm(EMPTY_FORM);
      fetchAccounts();
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Erro ao salvar', severity: 'error' });
    }
  };

  const handleDelete = async (a: ChartOfAccount) => {
    if (a.is_system) {
      setSnackbar({ open: true, message: 'Conta do sistema não pode ser deletada — apenas desativada.', severity: 'error' });
      return;
    }
    if (!window.confirm(`Excluir a conta "${a.code} · ${a.name}"? Filhas serão removidas em cascata.`)) return;
    try {
      await dreService.deleteChartOfAccount(a.id);
      setSnackbar({ open: true, message: 'Conta excluída', severity: 'success' });
      fetchAccounts();
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Erro ao excluir', severity: 'error' });
    }
  };

  const handleAutoClassify = async () => {
    try {
      const result = await dreService.autoClassify(false);
      const total = result.reduce((s, r) => s + (r.classified || 0), 0);
      setSnackbar({ open: true, message: `${total} registros classificados`, severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Erro', severity: 'error' });
    }
  };

  // ----------------- Renderiza nó da árvore --------------------------------
  const renderNode = (a: ChartOfAccount, depth: number = 0): React.ReactNode => {
    const children = tree.get(a.id) || [];
    const isOpen = expanded.has(a.id);
    return (
      <React.Fragment key={a.id}>
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            pl: 2 + depth * 3, pr: 2, py: 1,
            borderBottom: '1px solid #f1f5f9',
            bgcolor: depth === 0 ? '#fafbfc' : '#fff',
            '&:hover': { bgcolor: '#f1f5f9' },
            transition: 'background 0.15s',
            opacity: a.is_active ? 1 : 0.5,
          }}
        >
          {children.length > 0 ? (
            <IconButton size="small" onClick={() => toggle(a.id)} sx={{ p: 0.2 }}>
              {isOpen ? <ExpandIcon fontSize="small" /> : <CollapsedIcon fontSize="small" />}
            </IconButton>
          ) : <Box sx={{ width: 22 }} />}

          <Chip
            label={a.code}
            size="small"
            sx={{
              bgcolor: '#fff',
              border: '1px solid #cbd5e1', color: '#475569',
              fontWeight: 700, fontSize: '0.66rem', minWidth: 50,
              fontFamily: '"JetBrains Mono", monospace',
            }}
          />

          <Typography sx={{
            fontSize: depth === 0 ? '0.95rem' : '0.85rem',
            fontWeight: depth === 0 ? 800 : a.is_synthetic ? 700 : 500,
            color: '#0f172a', flex: 1, minWidth: 0,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {a.name}
          </Typography>

          <Chip
            label={DRE_GROUP_LABELS[a.dre_group]}
            size="small"
            sx={{
              bgcolor: `${DRE_GROUP_COLORS[a.dre_group]}15`,
              color: DRE_GROUP_COLORS[a.dre_group],
              fontWeight: 700, fontSize: '0.6rem', height: 20,
            }}
          />

          {a.auto_source && (
            <Tooltip title={`Auto: ${a.auto_source}`}>
              <Chip
                icon={<AutoIcon sx={{ fontSize: 12 }} />}
                label="auto"
                size="small"
                sx={{
                  bgcolor: '#eef2ff', color: '#4338ca',
                  fontWeight: 700, fontSize: '0.6rem', height: 20,
                  '& .MuiChip-icon': { color: '#4338ca' },
                }}
              />
            </Tooltip>
          )}

          {a.is_system && (
            <Tooltip title="Conta do sistema">
              <LockIcon fontSize="small" sx={{ color: '#94a3b8' }} />
            </Tooltip>
          )}

          {!a.is_active && (
            <Chip label="inativa" size="small" sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontSize: '0.6rem', height: 20 }} />
          )}

          <Box sx={{ display: 'flex', gap: 0.3 }}>
            {a.is_synthetic && (
              <Tooltip title="Adicionar conta filha">
                <IconButton size="small" onClick={() => handleCreate(a.id)}>
                  <AddIcon fontSize="small" sx={{ color: '#0C6FC1' }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => handleEdit(a)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {!a.is_system && (
              <Tooltip title="Excluir">
                <IconButton size="small" onClick={() => handleDelete(a)}>
                  <DeleteIcon fontSize="small" sx={{ color: '#dc2626' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Collapse in={isOpen}>
          {children.map((c) => renderNode(c, depth + 1))}
        </Collapse>
      </React.Fragment>
    );
  };

  const roots = tree.get(null) || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, gap: 1.5 }}>
      {/* ── Header ── */}
      <Paper
        elevation={0}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 2, py: 1, borderRadius: 2,
          bgcolor: '#fff', border: '1px solid #e2e8f0',
        }}
      >
        <IconButton size="small" onClick={() => navigate('/financial/dre')}>
          <BackIcon />
        </IconButton>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5,
          bgcolor: '#0C6FC1', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <AccountTreeIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: 1, lineHeight: 1 }}>
            Configuração do DRE
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>
            Plano de Contas
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<AutoIcon />}
          onClick={handleAutoClassify}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
        >
          Auto-classificar lançamentos
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleCreate(null)}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: '#0C6FC1', '&:hover': { bgcolor: '#0a5da0' } }}
        >
          Nova conta raiz
        </Button>
      </Paper>

      {/* ── Tree ── */}
      <Paper
        elevation={0}
        sx={{
          flex: 1, minHeight: 0, overflow: 'auto', borderRadius: 2,
          border: '1px solid #e2e8f0', bgcolor: '#fff',
        }}
      >
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={32} sx={{ color: '#0C6FC1' }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
        ) : roots.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Nenhuma conta cadastrada. Execute <code>06_seed_chart_of_accounts.sql</code> para o plano padrão.
            </Typography>
          </Box>
        ) : (
          roots.map((r) => renderNode(r))
        )}
      </Paper>

      {/* ── Dialog Form ── */}
      <Dialog
        open={!!editing || !!creating}
        onClose={() => { setEditing(null); setCreating(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #0C6FC1 0%, #0a5da0 100%)', color: '#fff',
          display: 'flex', alignItems: 'center', gap: 1, py: 1.5,
        }}>
          <AccountTreeIcon />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
              {editing ? 'Editar conta' : 'Nova conta'}
            </Typography>
            {creating?.parentId && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                Filha de: {accounts.find((a) => a.id === creating.parentId)?.name}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={() => { setEditing(null); setCreating(null); }} sx={{ color: '#fff' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              label="Código"
              value={form.code || ''}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
              size="small"
              sx={{ width: 140 }}
              required
            />
            <TextField
              label="Nome"
              value={form.name || ''}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              size="small"
              fullWidth
              required
            />
          </Box>

          <TextField
            label="Descrição (opcional)"
            value={form.description || ''}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            size="small"
            multiline
            minRows={2}
          />

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              label="Grupo DRE"
              value={form.dre_group || 'REVENUE'}
              onChange={(e) => setForm((f) => ({ ...f, dre_group: e.target.value as DreGroup }))}
              size="small"
              select
              fullWidth
              disabled={!!editing?.is_system}
            >
              {(Object.keys(DRE_GROUP_LABELS) as DreGroup[]).map((g) => (
                <MenuItem key={g} value={g}>{DRE_GROUP_LABELS[g]}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Natureza"
              value={form.nature || 'OPERATIONAL'}
              onChange={(e) => setForm((f) => ({ ...f, nature: e.target.value as DreNature }))}
              size="small"
              select
              fullWidth
            >
              <MenuItem value="OPERATIONAL">Operacional</MenuItem>
              <MenuItem value="INVESTING">Investimento</MenuItem>
              <MenuItem value="FINANCING">Financeira</MenuItem>
            </TextField>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              label="Sinal"
              value={form.sign === 1 ? '+1' : '-1'}
              onChange={(e) => setForm((f) => ({ ...f, sign: e.target.value === '+1' ? 1 : -1 }))}
              size="small"
              select
              sx={{ width: 100 }}
            >
              <MenuItem value="+1">+ 1</MenuItem>
              <MenuItem value="-1">− 1</MenuItem>
            </TextField>
            <TextField
              label="Ordem"
              value={form.sort_order || 0}
              onChange={(e) => setForm((f) => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
              size="small"
              type="number"
              sx={{ width: 110 }}
            />
            <TextField
              label="Auto-source"
              value={form.auto_source || ''}
              onChange={(e) => setForm((f) => ({ ...f, auto_source: e.target.value || null }))}
              size="small"
              select
              fullWidth
              helperText="Vincula a fato automático"
            >
              <MenuItem value="">— manual —</MenuItem>
              <MenuItem value="inventory_consumption">Consumo de estoque</MenuItem>
              <MenuItem value="commission">Comissão</MenuItem>
              <MenuItem value="professional_fee">Repasse</MenuItem>
              <MenuItem value="card_fee">Taxa de cartão</MenuItem>
              <MenuItem value="lab_payment">Laboratório</MenuItem>
              <MenuItem value="nfse_iss">ISS NFSe</MenuItem>
            </TextField>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControlLabel
              control={<Switch checked={!!form.is_synthetic} onChange={(_, v) => setForm((f) => ({ ...f, is_synthetic: v }))} />}
              label="Sintética (recebe filhas)"
            />
            <FormControlLabel
              control={<Switch checked={form.is_active !== false} onChange={(_, v) => setForm((f) => ({ ...f, is_active: v }))} />}
              label="Ativa"
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => { setEditing(null); setCreating(null); }}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, bgcolor: '#0C6FC1', '&:hover': { bgcolor: '#0a5da0' } }}
            disabled={!form.code || !form.name}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2, fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChartOfAccounts;
